import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  AppView,
  CalibrationSession,
  CleaningOptions,
  CleaningReport,
  DiagnosticReport,
  LUT3D,
  LUTOptions,
  MeasuredPatch,
  ParsedCGATS,
  StepStatus,
  WorkingDomain,
} from '@/types';
import {
  DEFAULT_CLEANING_OPTIONS,
  DEFAULT_LUT_OPTIONS,
} from '@/types';

// ---------------------------------------------------------------------------
// Store shape
// ---------------------------------------------------------------------------

interface AppState {
  currentView: AppView;
  session: CalibrationSession;

  // Actions — navigation
  setView: (view: AppView) => void;

  // Actions — import
  setCGATS:    (cgats: ParsedCGATS) => void;
  setRawPatches: (patches: MeasuredPatch[]) => void;

  // Actions — cleaning
  setCleaningOptions: (opts: Partial<CleaningOptions>) => void;
  setCleaningResult:  (patches: MeasuredPatch[], report: CleaningReport) => void;

  // Actions — diagnostics
  setDiagnostics: (report: DiagnosticReport) => void;

  // Actions — domain
  setDomain: (domain: WorkingDomain) => void;

  // Actions — LUT
  setLUTOptions: (opts: Partial<LUTOptions>) => void;
  setLUT:        (lut: LUT3D) => void;

  // Actions — step status
  setStepStatus: (step: keyof CalibrationSession['stepStatus'], status: StepStatus) => void;

  // Actions — session reset
  resetSession: () => void;
}

// ---------------------------------------------------------------------------
// Initial session
// ---------------------------------------------------------------------------

function makeInitialSession(): CalibrationSession {
  const now = new Date().toISOString();
  return {
    id:        crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,

    rawCGATS:       null,
    rawPatches:     null,

    cleaningOptions: DEFAULT_CLEANING_OPTIONS,
    cleaningReport:  null,
    patches:         null,

    diagnostics: null,
    domain:      null,

    lutOptions: DEFAULT_LUT_OPTIONS,
    lut:        null,

    profileMeta: null,
    deviceLink:  null,
    validation:  null,

    stepStatus: {
      import:   'idle',
      diagnose: 'idle',
      domain:   'idle',
      build:    'idle',
      export:   'idle',
      apply:    'idle',
    },
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAppStore = create<AppState>()(
  immer((set) => ({
    currentView: 'import',
    session:     makeInitialSession(),

    setView: (view) => set((s) => { s.currentView = view; }),

    setCGATS: (cgats) => set((s) => {
      s.session.rawCGATS  = cgats;
      s.session.updatedAt = new Date().toISOString();
      s.session.stepStatus.import = 'ready';
    }),

    setRawPatches: (patches) => set((s) => {
      s.session.rawPatches = patches;
      s.session.updatedAt  = new Date().toISOString();
    }),

    setCleaningOptions: (opts) => set((s) => {
      Object.assign(s.session.cleaningOptions, opts);
    }),

    setCleaningResult: (patches, report) => set((s) => {
      s.session.patches        = patches;
      s.session.cleaningReport = report;
      s.session.updatedAt      = new Date().toISOString();
      s.session.stepStatus.import   = 'done';
      s.session.stepStatus.diagnose = 'ready';
    }),

    setDiagnostics: (report) => set((s) => {
      s.session.diagnostics    = report;
      s.session.updatedAt      = new Date().toISOString();
      s.session.stepStatus.diagnose = 'done';
      s.session.stepStatus.domain   = 'ready';
    }),

    setDomain: (domain) => set((s) => {
      s.session.domain    = domain;
      s.session.updatedAt = new Date().toISOString();
      s.session.stepStatus.domain = 'done';
      s.session.stepStatus.build  = 'ready';
    }),

    setLUTOptions: (opts) => set((s) => {
      Object.assign(s.session.lutOptions, opts);
    }),

    setLUT: (lut) => set((s) => {
      s.session.lut       = lut;
      s.session.updatedAt = new Date().toISOString();
      s.session.stepStatus.build  = 'done';
      s.session.stepStatus.export = 'ready';
      s.session.stepStatus.apply  = 'ready';
    }),

    setStepStatus: (step, status) => set((s) => {
      s.session.stepStatus[step] = status;
    }),

    resetSession: () => set((s) => {
      s.session     = makeInitialSession();
      s.currentView = 'import';
    }),
  })),
);
